import { PopperProps } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import { base_colorSurfaceMutedHover } from 'ext-ui/style/colors'
import ReceiptIcon from 'ext-ui/svg/icon/ReceiptIcon'
import ReceiptMismatchIcon from 'ext-ui/svg/icon/ReceiptMismatchIcon'
import React from 'react'
import { useDispatch } from 'react-redux'
import AttachmentVerificationTooltip from 'src/components/AttachmentVerificationTooltip/AttachmentVerificationTooltip'
import { useTranslate } from 'src/localization/Trns'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import { ReceiptVerificationStatus } from 'src/models/receiptVerification.models'
import {
  TransactionDetailModel,
  TransactionDetailResponse,
} from 'src/models/transaction.models'
import { transactionsQueryKeys } from 'src/queries/queries.constants'
import { useRequestConfig } from 'src/queries/queries.utils'
import { createSetIsAttachmentPopupOpen } from 'src/redux/actions/app.actions'
import { css } from 'styled-components'

interface ReceiptsTooltipProps {
  count?: number
  placement: PopperProps['placement']
  isMissing?: boolean
  verificationStatus?: ReceiptVerificationStatus
  verificationReasons?: string[]
  attachment?: ReceiptAttachmentModel
  transactionId?: string
}

const ReceiptsTooltip: React.FC<ReceiptsTooltipProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const requestConfig = useRequestConfig()

  const hasVerification =
    props.verificationStatus === ReceiptVerificationStatus.Verified ||
    props.verificationStatus === ReceiptVerificationStatus.AutoMatched ||
    props.verificationStatus === ReceiptVerificationStatus.NotVerified ||
    props.verificationStatus === ReceiptVerificationStatus.Mismatched ||
    props.verificationStatus === ReceiptVerificationStatus.Unreadable

  let message: React.ReactNode
  if (hasVerification) {
    message = (
      <AttachmentVerificationTooltip
        status={props.verificationStatus}
        reasons={props.verificationReasons}
      />
    )
  } else if (props.isMissing) {
    message = translate('Missing Receipt')
  } else if (props.count !== undefined) {
    message = translate(
      props.count === 1 ? '${count} receipt' : '${count} receipts',
      { count: props.count.toString() },
    )
  }

  const handleOpenAttachment = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    event.preventDefault()
    event.stopPropagation()
    if (props.isMissing) {
      return
    }
    if (props.attachment !== undefined) {
      dispatch(
        createSetIsAttachmentPopupOpen(true, undefined, props.attachment),
      )
      return
    }
    if (props.transactionId !== undefined) {
      queryClient
        .fetchQuery({
          queryKey: transactionsQueryKeys.detail(props.transactionId),
          queryFn: () =>
            axios.get<TransactionDetailResponse>(
              `/transactions/${props.transactionId}`,
              requestConfig,
            ),
          retry: false,
        })
        .then((response) => {
          const detail = new TransactionDetailModel(response.data)
          const firstAttachment = detail.receiptAttachments[0]
          if (firstAttachment !== undefined) {
            dispatch(
              createSetIsAttachmentPopupOpen(true, undefined, firstAttachment),
            )
          }
        })
        .catch(() => undefined)
    }
  }

  const receiptIcon = (
    <span
      css={css`
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 8px;
        border-radius: 8px;
        &:hover {
          background: ${base_colorSurfaceMutedHover};
        }
      `}
    >
      {props.isMissing ? (
        <ReceiptMismatchIcon width={16} height={16} />
      ) : (
        <ReceiptIcon width={16} height={16} />
      )}
    </span>
  )

  const tooltipIcon = (
    <TooltipIcon
      placement={props.placement}
      icon={receiptIcon}
      iconTestId={
        props.isMissing ? 'missingReceiptsIcon' : 'receiptsAttachedIcon'
      }
    >
      {message}
    </TooltipIcon>
  )

  const canOpenAttachment =
    !props.isMissing &&
    (props.attachment !== undefined || props.transactionId !== undefined)

  return canOpenAttachment ? (
    <button
      type="button"
      onClick={handleOpenAttachment}
      css={css`
        border: none;
        padding: 0;
        margin: 0;
        background: transparent;
        cursor: pointer;
        display: flex;
        align-items: center;
      `}
    >
      {tooltipIcon}
    </button>
  ) : (
    tooltipIcon
  )
}

export default ReceiptsTooltip
